import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { galleryEntries } from './api/bootstrap.js'
import shell from './App.module.css'
import { LOADING_EXIT_MS, LoadingScreen } from './boot/LoadingScreen.js'
import { assetSrc, useAssetLoader } from './boot/useAssetLoader.js'
import { invitation } from './data/invitation.js'
import { useBodyScrollLock } from './hooks/useBodyScrollLock.js'
import { Toast, useToast } from './hooks/useToast.js'
import { ScrollProgress } from './ScrollProgress.js'
import { SectionBoundary } from './SectionBoundary.js'
import { CeremonyInfo } from './sections/CeremonyInfo.js'
import { Contact } from './sections/Contact.js'
import { Couple } from './sections/Couple.js'
import { Directions } from './sections/Directions.js'
import { EndingFilm } from './sections/EndingFilm.js'
import endingFirstFrame from './sections/film-assets/ending-first-frame.webp'
import { EnvelopeIntro } from './sections/EnvelopeIntro.js'
import { Footer } from './sections/Footer.js'
import { Gallery } from './sections/Gallery.js'
import { GiftAccounts } from './sections/GiftAccounts.js'
import { GuestSnap } from './sections/GuestSnap.js'
import { HeroFilm, type HeroFilmHandle } from './sections/HeroFilm.js'
import { Invitation } from './sections/Invitation.js'

type ScreenState = 'loading' | 'exiting' | 'hidden'

/** 섹션 하나가 던져도 그 섹션만 사라진다 — 문서는 계속 열려 있다. */
function Section({ children }: { children: ReactNode }) {
  return <SectionBoundary>{children}</SectionBoundary>
}

export function App() {
  const hero = useRef<HeroFilmHandle>(null)
  const heroVideo = useRef<HTMLVideoElement>(null)
  const contentStart = useRef<HTMLDivElement>(null)
  const contentEnd = useRef<HTMLDivElement>(null)
  const endingEnd = useRef<HTMLDivElement>(null)
  const loader = useAssetLoader()
  // loader.phase가 첫 렌더에 이미 'ready'라면(예: critical 자산이 없는 로드
  // 플랜) 로딩 화면을 잠깐 보여줬다가 곧장 페이드아웃시키지 않는다 — 처음부터
  // 숨긴 채로 시작한다. 지금의 useAssetLoader는 phase를 항상 마운트 이후
  // effect에서 비동기로 올리므로 실제로 여기 걸리진 않지만, 이 초기값은
  // useAssetLoader가 나중에 바뀌더라도(혹은 테스트에서 훅을 스텁하더라도)
  // 깜빡임 없이 옳게 동작하도록 loader.phase에서 유도한다.
  const [screenState, setScreenState] = useState<ScreenState>(() =>
    loader.phase === 'ready' ? 'hidden' : 'loading',
  )
  const [envelopeOpened, setEnvelopeOpened] = useState(false)
  const [heroScrollMode, setHeroScrollMode] = useState(false)
  const handleHeroScrollMode = useCallback(() => setHeroScrollMode(true), [])
  const handleEnvelopeOpening = useCallback(() => hero.current?.play(), [])
  const handleEnvelopeOpened = useCallback(() => setEnvelopeOpened(true), [])

  useEffect(() => {
    if (loader.phase === 'ready' && screenState === 'loading') {
      setScreenState('exiting')
    }
  }, [loader.phase, screenState])

  useEffect(() => {
    if (screenState !== 'exiting') return
    // transitionend가 아니라 타이머로 언마운트한다. jsdom은 CSS 트랜지션을 아예
    // 실행하지 않고, 실기기에서도 리듀스드 모션 설정이나 놓친 애니메이션
    // 이벤트 때문에 로딩 화면이 하객을 영원히 붙잡아두면 안 된다 — 타이머는
    // 애니메이션 이벤트 도착 여부와 무관하게 항상 발화한다.
    const timer = setTimeout(() => setScreenState('hidden'), LOADING_EXIT_MS)
    return () => clearTimeout(timer)
  }, [screenState])

  // 청첩장은 로딩 화면 뒤에 이미 마운트되어 있다. 잠그지 않으면 로딩 중에 쓸어
  // 올린 하객이 페이드가 끝나는 순간 문서 한가운데(히어로 스크럽은 이미 끝까지
  // 감긴 채)에 서 있게 된다. 새로고침 시 브라우저 스크롤 복원도 같은 일을 한다.
  useBodyScrollLock(screenState !== 'hidden')

  // blob이 없으면(다운로드 실패·용량 초과·'그냥 들어가기') 스트리밍 소스로 붙는다.
  // 받는 중에는 아무것도 붙이지 않는다 — 같은 파일을 두 번 받지 않기 위해서다(스펙 §9).
  const heroSrc = assetSrc(loader, 'hero')
  const mainSrc = assetSrc(loader, 'main')
  const endingSrc = assetSrc(loader, 'ending')
  // blob이 있을 때만 통째로 읽는다. 스트리밍 소스에 preload="auto"를 걸면
  // 백그라운드 워밍과 같은 파일을 동시에 두 번 받는다.
  const endingPreload = loader.assets.ending === undefined ? 'metadata' : 'auto'

  const { toast, show } = useToast()

  return (
    <>
      {screenState !== 'hidden' && (
        <LoadingScreen
          progress={loader.progress}
          escapeAvailable={loader.escapeAvailable}
          onEnter={loader.enterAnyway}
          exiting={screenState === 'exiting'}
        />
      )}
      <EnvelopeIntro
        interactive={screenState === 'hidden'}
        onOpening={handleEnvelopeOpening}
        onOpened={handleEnvelopeOpened}
      />
      <ScrollProgress
        active={envelopeOpened}
        heroVideoRef={heroVideo}
        heroScrollMode={heroScrollMode}
        startRef={contentStart}
        endRef={contentEnd}
        endingEndRef={endingEnd}
      />
      {/* 로딩 중에도 마운트되어 있어야 페이드아웃 시점에 히어로 첫 프레임이
          이미 깔려 있다. */}
      <main className={shell.paper}>
        {/* 디자인 원본에는 문서 제목이 놓일 자리가 없다. 문서 구조에는 있어야 한다. */}
        <h1 className={shell.srOnly}>
          {invitation.couple.groom.name} ♥ {invitation.couple.bride.name} 결혼합니다
        </h1>
        <Section>
          <HeroFilm
            ref={hero}
            videoRef={heroVideo}
            src={heroSrc}
            poster={mainSrc}
            nextRef={contentStart}
            onScrollMode={handleHeroScrollMode}
          />
        </Section>
        <div
          ref={contentStart}
          className={shell.progressBoundary}
          data-scroll-progress-boundary="start"
          aria-hidden="true"
        />
        <Section>
          <Invitation />
        </Section>
        <Section>
          <Couple src={mainSrc} />
        </Section>
        <Section>
          <CeremonyInfo />
        </Section>
        <Section>
          <Gallery entries={galleryEntries(loader.bootstrap)} />
        </Section>
        <Section>
          <Directions onCopied={show} />
        </Section>
        <Section>
          <Contact contact={loader.bootstrap.contact} />
        </Section>
        <Section>
          <GiftAccounts contact={loader.bootstrap.contact} onCopied={show} />
        </Section>
        <Section>
          <GuestSnap openAt={loader.bootstrap.snapUploadOpenAt} onNotify={show} />
        </Section>
        <div
          ref={contentEnd}
          className={shell.progressBoundary}
          data-scroll-progress-boundary="end"
          aria-hidden="true"
        />
        <Section>
          <EndingFilm src={endingSrc} poster={endingFirstFrame} preload={endingPreload} />
        </Section>
        <div
          ref={endingEnd}
          className={shell.progressBoundary}
          data-scroll-progress-boundary="ending-end"
          aria-hidden="true"
        />
        <Section>
          <Footer />
        </Section>
      </main>
      {toast !== null && <Toast message={toast} />}
    </>
  )
}
